import type Ajv from "ajv";
import type { JsonSchema } from "@jsonforms/core";
import type { CatalogFieldState } from "@/lib/api/generated/casedb";
import type { CaseFieldValues } from "../model/caseReview";
import type { ReviewDecision } from "../constants/caseReviewDecisions";
import type { FieldKeyResolver } from "./caseReviewFieldState";
import { isAcceptable, isFieldEditable } from "./caseReviewAcceptAll";
import { conditionalRequiredFields } from "./catalogConditionalRules";
import { nestRecordValues, valueAt } from "./caseReviewRecords";
import { RECORD_ID_PROPERTY } from "../constants/catalogRecordIdentity";

/** Somewhere a reviewer still has to go: a field, and the record holding it when there is one. */
export interface OutstandingTarget {
  field_id: string;
  record_id?: string;
  /** The row holding a nested row, as on a finding: product 1 and substance row 1 are different rows (#286). */
  owner_record_id?: string;
}

/**
 * One scalar section or one record, with everything needed to ask what is outstanding in it.
 *
 * <p>The same shape for both, because "the next thing to look at" does not care which it is —
 * only the reader assembling these knows the difference.
 */
export interface ReviewForm {
  sectionId: string;
  recordId?: string;
  /**
   * The field-id prefix of a record's collection (`case.product.products.`). A child record and
   * its parent can share a numeric id, so the counts key a record form by this and its id.
   */
  collectionPrefix?: string;
  /** The record that holds this one, for a nested row such as a product's substance (#286). */
  ownerRecordId?: string;
  /** The resolved schema for this form: configured mandatory fields and compiled rules included. */
  schema: JsonSchema | undefined;
  values: CaseFieldValues;
  decisions: Record<string, ReviewDecision>;
  states: Record<string, CatalogFieldState>;
  /** Field id to control key, for the states this form owns. */
  keyOf: FieldKeyResolver;
  /** Control key back to field id, for naming a target the navigator can resolve. */
  fieldIdOf: (key: string) => string;
  /** The section-level and field-level restrictions the renderer also applies. */
  sectionEditable: boolean;
  disabledFields: Record<string, string>;
  /** Whether Theragenie has a pending proposal on this field; a proposed field is not manual entry. */
  hasProposal?: (fieldId: string) => boolean;
}

/**
 * AI values in this form that nobody has decided on, in the schema's own order.
 *
 * <p>Same predicate as Accept — a review row exists and no decision has been taken — so "next
 * unreviewed" can never land on a field whose Accept the screen does not offer.
 */
export function unreviewedIn(form: ReviewForm): OutstandingTarget[] {
  return Object.entries(form.states)
    .filter(([fieldId, state]) => {
      const key = form.keyOf(fieldId);
      return !!key && isFieldEditable(form, fieldId, key) && isAcceptable(state, form.decisions[key]);
    })
    .map(([fieldId]) => target(fieldId, form.recordId, form.ownerRecordId));
}

/**
 * Required fields in this form that are still empty.
 *
 * <p>Two sources, and both matter: the schema's own `required` list, which the resolver has already
 * filled in from the configured mandatory rules, and the conditional rules, asked through the same
 * helper the row controls use so a rule that has just become applicable counts immediately — a
 * field required only because the reviewer ticked something a moment ago is exactly the one they
 * are about to miss.
 */
export function unmetRequiredIn(form: ReviewForm, ajv: Ajv): OutstandingTarget[] {
  return requiredFieldsIn(form, ajv)
    .filter((field) => !field.filled)
    .filter((field) => isFieldEditable(form, field.fieldId, field.key))
    .map((field) => target(field.fieldId, form.recordId, form.ownerRecordId));
}

/** A required field that applies right now, and whether it has an answer. */
export interface RequiredField { key: string; fieldId: string; filled: boolean }

/**
 * Every required field that applies to this form right now, filled or not — the one rule the
 * counts, "Next required" and the route gate share.
 *
 * <p>Editability is deliberately not applied here: a field the user cannot change is still a field
 * the case needs. Only navigation (`unmetRequiredIn`) leaves it out.
 */
export function requiredFieldsIn(form: Pick<ReviewForm, "schema" | "values" | "fieldIdOf">, ajv: Ajv): RequiredField[] {
  if (!form.schema) return [];
  // Without $id. Every generated schema carries one, and asking a single Ajv instance to compile
  // more than one of them throws "schema with key or id already exists" — which, thrown inside a
  // click handler, looks exactly like a button that does nothing. The server's own record
  // validator drops $id for the same reason.
  const { $id: _id, ...rest } = form.schema as Record<string, unknown>;
  // `type: "object"` because a form schema is one, and Ajv's strict mode refuses `required`
  // without it. A resolved record schema does not always carry it — it is a fragment of the
  // section's own document, where the type was stated one level up.
  const schema = { type: "object", ...rest } as JsonSchema;
  // Record drafts deliberately use dotted control keys. JSON Schema rules, however, evaluate
  // their object hierarchy; put the same draft projection back into objects before walking it.
  const values = nestRecordValues(form.values) as CaseFieldValues;
  return requiredKeys(schema, values, ajv)
    // The record's identity is required so a PATCH can address the row, not because anyone answers
    // it. It renders no control, so offering it as somewhere to go sends the reviewer nowhere —
    // which is what the route modal's first live capture showed, three times over, one per record.
    .filter((key) => !key.split(".").includes(RECORD_ID_PROPERTY))
    .map((key) => ({ key, fieldId: form.fieldIdOf(key), filled: !isEmpty(valueAt(values, key)) }));
}

/** Required paths in one resolved form, including object properties and their local conditional rules. */
function requiredKeys(schema: JsonSchema, values: CaseFieldValues, ajv: Ajv, prefix = ""): string[] {
  const required = new Set<string>([
    ...(schema.required ?? []).map((key) => `${prefix}${key}`),
    ...[...conditionalRequiredFields(schema, values, ajv)].map((key) => `${prefix}${key}`),
  ]);
  for (const [key, property] of Object.entries(schema.properties ?? {})) {
    const objectType = Array.isArray(property.type) ? property.type.includes("object") : property.type === "object";
    if (!objectType) continue;
    const nested = valueAt(values, key);
    if (!nested || typeof nested !== "object" || Array.isArray(nested)) continue;
    requiredKeys(property, nested as CaseFieldValues, ajv, `${prefix}${key}.`)
      .forEach((nestedKey) => required.add(nestedKey));
  }
  return [...required];
}

/** Empty is absent, null, a string of nothing, or a list with no rows; `false` and `0` are answers. */
function isEmpty(value: CaseFieldValues[string]): boolean {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "")
    || (Array.isArray(value) && value.length === 0);
}

function target(fieldId: string, recordId?: string, ownerRecordId?: string): OutstandingTarget {
  if (recordId === undefined) return { field_id: fieldId };
  return ownerRecordId === undefined ? { field_id: fieldId, record_id: recordId }
    : { field_id: fieldId, record_id: recordId, owner_record_id: ownerRecordId };
}
